import { memo, useId, useMemo } from 'react';
import styles from './Sparkline.module.css';

interface Props {
  readonly data: readonly number[];
  readonly width?: number;
  readonly height?: number;
  readonly stroke?: string;
  /** Accessible name. SVG is otherwise marked aria-hidden. */
  readonly label?: string;
}

/**
 * Minimal SVG line+area chart. Memoised — pure function of `data + dims`.
 * Empty/single-point/all-equal inputs degrade gracefully.
 */
export const Sparkline = memo(function Sparkline({
  data,
  width = 96,
  height = 28,
  stroke = 'var(--pfl-magenta-bright)',
  label,
}: Props): JSX.Element | null {
  const gradId = useId();

  const geometry = useMemo(() => {
    if (data.length === 0) return null;
    let min = data[0] ?? 0;
    let max = data[0] ?? 0;
    for (const n of data) {
      if (n < min) min = n;
      if (n > max) max = n;
    }
    const span = max - min || 1;
    const denom = Math.max(data.length - 1, 1);
    const step = width / denom;
    const points = data
      .map((d, i) => {
        const y = height - ((d - min) / span) * (height - 2) - 1;
        return `${String(i * step)},${y.toFixed(2)}`;
      })
      .join(' ');
    const area = `0,${String(height)} ${points} ${String(width)},${String(height)}`;
    return { points, area };
  }, [data, width, height]);

  if (!geometry) return null;

  const ariaProps =
    label !== undefined
      ? { role: 'img' as const, 'aria-label': label }
      : { 'aria-hidden': true as const };

  return (
    <svg
      className={styles.spark}
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      {...ariaProps}
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.45" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={geometry.area} fill={`url(#${gradId})`} stroke="none" />
      <polyline
        points={geometry.points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
});
